import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import { Bell } from "@/components/icons/";
import Button from "@/components/ui/button";

import { useNotifications } from "../hooks/useNotifications";
import { notificationsApi } from "../services/notifications.api";
import type { NotificationItem } from "../types/notifications.types";
import { resolveNotificationRoute } from "../utils/route-resolver";

import styles from "./NotificationsPage.module.scss";

/**
 * Full notifications list page.
 *
 * Renders at /dashboard/notifications. Supports:
 *  - tab filter: Tümü / Okunmamış (maps to `?unreadOnly=true`)
 *  - pagination (page/limit)
 *  - per-row click -> mark-as-read + navigate
 *  - bulk mark-all-as-read
 */
const NotificationsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { items, total, unreadCount, page, limit, unreadOnly, isLoading, refetch, setPage, setUnreadOnly } =
    useNotifications({ page: 1, limit: 20 });

  const [busy, setBusy] = useState(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / (limit || 20))),
    [total, limit],
  );

  const handleRowClick = async (n: NotificationItem) => {
    if (!n.readAt) {
      try {
        await notificationsApi.markNotificationAsRead(n.id);
      } catch {
        /* toast already shown by axios interceptor */
      }
      await refetch();
    }
    const route = resolveNotificationRoute(n);
    if (route) navigate(route);
  };

  const handleMarkAllAsRead = async () => {
    setBusy(true);
    try {
      await notificationsApi.markAllNotificationsAsRead();
      await refetch();
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout>
      <div className={styles.page}>
      <Header
        title={t("notifications.title")}
        description={t("notifications.pageDescription")}
        icon={<Bell width={24} height={24} />}
      />

      <div className={styles.toolbar}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${!unreadOnly ? styles.active : ""}`}
            onClick={() => setUnreadOnly(false)}
            type="button"
          >
            {t("notifications.tabAll")}
          </button>
          <button
            className={`${styles.tab} ${unreadOnly ? styles.active : ""}`}
            onClick={() => setUnreadOnly(true)}
            type="button"
          >
            {t("notifications.tabUnread")}
            {unreadCount > 0 && (
              <span className={styles.tabBadge}>
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
        </div>

        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={handleMarkAllAsRead}
          disabled={busy || unreadCount === 0}
          loading={busy}
        >
          {t("notifications.markAllAsRead")}
        </Button>
      </div>

      <div className={styles.listCard}>
        {isLoading && (
          <div className={styles.empty}>
            {t("common.loading")}
          </div>
        )}
        {!isLoading && items.length === 0 && (
          <div className={styles.empty}>
            {t("notifications.empty")}
          </div>
        )}
        {!isLoading &&
          items.map((n) => {
            const unread = !n.readAt;
            return (
              <div
                key={n.id}
                className={`${styles.row} ${unread ? styles.unread : ""}`}
                onClick={() => void handleRowClick(n)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    void handleRowClick(n);
                  }
                }}
              >
                {unread ? (
                  <div className={styles.rowDot} />
                ) : (
                  <div className={styles.rowDotPlaceholder} />
                )}
                <div className={styles.rowBody}>
                  {n.title && <h4 className={styles.rowTitle}>{n.title}</h4>}
                  <p className={styles.rowMessage}>{n.message}</p>
                  <div className={styles.rowMeta}>
                    {new Date(n.createdAt).toLocaleString()}
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {total > limit && (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.pageBtn}
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            {t("common.previous")}
          </button>
          <span style={{ fontSize: 13, color: "#505766" }}>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            className={styles.pageBtn}
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            {t("common.next")}
          </button>
        </div>
      )}
      </div>
    </DashboardLayout>
  );
};

export default NotificationsPage;
